import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Sun, Trophy, Zap, LayoutGrid, Globe, ArrowRight, Star, Pin, RotateCcw } from 'lucide-react';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { useTheme } from '../hooks/useTheme';
import { cn } from '../components/ui/Button';
export function OverviewView() {
  const navigate = useNavigate();
  const {
    workspaces,
    setActiveWorkspace
  } = useWorkspaces();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  // If the product tour hasn't been completed, redirect to Athena where the tour runs
  useEffect(() => {
    const tourCompleted = localStorage.getItem('jua-tour-completed') === 'true';
    if (!tourCompleted) {
      navigate('/athena', {
        replace: true
      });
    }
  }, [navigate]);
  const pinnedWorkspaces = workspaces.filter(w => w.pinned || w.favorited).slice(0, 4);
  const recentWorkspaces = workspaces.filter(w => !w.pinned && !w.favorited).slice(0, 4);
  const displayWorkspaces = pinnedWorkspaces.length > 0 ? pinnedWorkspaces : recentWorkspaces;
  const EXPLORE_OPTIONS = [{
    icon: Sparkles,
    title: 'Ask Athena',
    desc: 'Chat with Athena about forecasts, analysis, and trading signals',
    path: '/athena'
  }, {
    icon: Sun,
    title: 'Weather Forecasting',
    desc: 'Explore multi-model forecasts for wind, solar, and temperature',
    path: '/energy'
  }, {
    icon: Trophy,
    title: 'Benchmarking',
    desc: 'Compare model accuracy across regions and variables',
    path: '/stations'
  }, {
    icon: LayoutGrid,
    title: 'Discover Workspaces',
    desc: 'Build a custom dashboard with charts, maps, and tables',
    path: '/workspaces'
  }, {
    icon: Globe,
    title: 'Interactive Map',
    desc: 'Visualize weather data across Europe on a live map',
    path: '/map'
  }];
  return <div className="jua-overview-page jua-page-bg flex flex-col h-[calc(100vh-64px)] overflow-hidden relative" data-id="element-4172">
      <div className="flex-1 overflow-y-auto" style={{
      background: isLight ? 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(236,72,153,0.08) 0%, rgba(168,85,247,0.05) 35%, rgba(99,102,241,0.03) 55%, transparent 75%)' : 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(236,72,153,0.18) 0%, rgba(168,85,247,0.10) 35%, rgba(99,102,241,0.07) 55%, transparent 75%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(56,189,248,0.07) 0%, rgba(168,85,247,0.05) 40%, transparent 70%), radial-gradient(ellipse 50% 40% at 10% 90%, rgba(236,72,153,0.08) 0%, transparent 60%)'
    }} data-id="element-4173">
        <div className="flex flex-col items-center min-h-full px-8 py-4 md:py-8 max-w-4xl mx-auto w-full pt-6 md:pt-12" data-id="element-4174">
          <div className="flex-1 flex flex-col items-center justify-start md:justify-center w-full pt-4 md:pt-0" data-id="element-4175">
            <div className="text-center mb-8" data-id="element-4176">
              <div className="w-16 h-16 mx-auto flex items-center justify-center mb-5" data-id="element-4177">
                <img src="https://cdn.magicpatterns.com/uploads/6Vk965L7QqZBGFchHxJDat/pasted-image.png" alt="Jua logo" className="w-16 h-16 object-contain" data-id="element-4178" />
              </div>
              <h1 className={cn('text-3xl font-bold tracking-tight mb-2', isLight ? 'text-slate-900' : 'text-white')} data-id="element-4179">
                Where would you like to go?
              </h1>
              <p className={cn('text-base max-w-md mx-auto', isLight ? 'text-slate-600' : 'text-slate-400')} data-id="element-4180">
                Jump straight into any part of the platform, or let Athena guide
                you.
              </p>
              <button onClick={() => {
              localStorage.removeItem('jua-tour-completed');
              navigate('/athena', {
                replace: true
              });
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('jua-restart-tour'));
              }, 500);
            }} className={cn('inline-flex items-center gap-1.5 mt-3 text-xs font-medium transition-colors', isLight ? 'text-[#EC4899]/70 hover:text-[#EC4899]' : 'text-[#EC4899]/60 hover:text-[#EC4899]')} data-id="element-4181">
                <RotateCcw size={12} data-id="element-4182" />
                Replay product tour
              </button>
            </div>

            <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-id="element-4183">
              {EXPLORE_OPTIONS.map((opt, i) => <motion.button key={opt.title} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 + i * 0.06,
              duration: 0.35
            }} onClick={() => navigate(opt.path)} className={cn('group flex flex-col items-start gap-3 p-4 rounded-xl transition-all text-left', isLight ? 'bg-white/60 backdrop-blur-xl border border-white shadow-[0_2px_8px_rgba(148,163,184,0.15)] hover:bg-white/80 hover:shadow-md' : 'bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.07] hover:border-white/[0.18]')} data-id="element-4184">
                  <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center border backdrop-blur-sm', isLight ? 'bg-white/80 border-white shadow-sm' : 'bg-white/[0.06] border-white/[0.08] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]')} data-id="element-4185">
                    <opt.icon size={18} className={isLight ? 'text-slate-500/70 group-hover:text-slate-700' : 'text-white/70 group-hover:text-white/90'} data-id="element-4186" />
                  </div>
                  <div data-id="element-4187">
                    <p className={cn('text-sm font-medium transition-colors mb-0.5', isLight ? 'text-slate-800 group-hover:text-slate-900' : 'text-white/90 group-hover:text-white')} data-id="element-4188">
                      {opt.title}
                    </p>
                    <p className={cn('text-[11px] transition-colors leading-relaxed', isLight ? 'text-slate-500 group-hover:text-slate-600' : 'text-slate-500 group-hover:text-slate-400')} data-id="element-4189">
                      {opt.desc}
                    </p>
                  </div>
                  <ArrowRight size={14} className={cn('transition-all mt-auto', isLight ? 'text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5' : 'text-slate-600 group-hover:text-white/50 group-hover:translate-x-0.5')} data-id="element-4190" />
                </motion.button>)}
            </div>
          </div>

          {/* Workspaces quick-access — pinned to bottom */}
          <div className="w-full mt-auto pt-3 md:pt-6 pb-2 md:pb-[5vh]" data-id="element-4191">
            <div className="flex items-center justify-between mb-3" data-id="element-4192">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-4193">
                <LayoutGrid size={12} data-id="element-4194" />
                <span data-id="element-4195">Your Workspaces</span>
              </div>
              <button onClick={() => navigate('/workspaces')} className="text-[11px] text-slate-500 hover:text-[#EC4899] transition-colors" data-id="element-4196">
                View all →
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-4197">
              {displayWorkspaces.map(ws => <button key={ws.id} onClick={() => {
              setActiveWorkspace(ws.id);
              navigate(`/workspace/${ws.id}`);
            }} className={cn('jua-overview-ws-card flex flex-col items-start gap-1.5 p-3 rounded-xl transition-all text-left group backdrop-blur-sm shadow-sm', isLight ? 'bg-black/[0.03] hover:bg-black/[0.06] border border-black/[0.06] hover:border-black/[0.12]' : 'bg-white/[0.05] hover:bg-white/[0.09] border border-white/[0.09] hover:border-white/[0.16]')} data-id="element-4198">
                  <div className="flex items-center justify-between w-full" data-id="element-4199">
                    <div className="w-6 h-6 rounded-md bg-[#EC4899]/10 border border-[#EC4899]/20 flex items-center justify-center text-[10px] font-bold text-[#EC4899]" data-id="element-4200">
                      {ws.name.charAt(0)}
                    </div>
                    {ws.favorited && <Star size={10} className="text-amber-400 fill-amber-400" data-id="element-4201" />}
                    {ws.pinned && !ws.favorited && <Pin size={10} className="text-slate-500" data-id="element-4202" />}
                  </div>
                  <span className={cn('text-xs font-medium transition-colors line-clamp-1 leading-tight', isLight ? 'text-slate-700 group-hover:text-slate-900' : 'text-slate-300 group-hover:text-white')} data-id="element-4203">
                    {ws.name}
                  </span>
                  <span className="text-[10px] text-slate-600 line-clamp-1" data-id="element-4204">
                    {ws.description}
                  </span>
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}